"use client";

import * as React from "react";

/**
 * The element that popups opened inside a modal layer portal into. A Radix
 * modal layer turns pointer events off on `<body>`, so a popup portaled there
 * stays visible but ignores the mouse; one portaled inside the layer keeps
 * working. `undefined` means no layer is open, and `null` that the layer's
 * element hasn't mounted yet, which makes a popup wait for it.
 */
const PortalContainerContext = React.createContext<
  HTMLElement | null | undefined
>(undefined);

/** The portal container of the nearest open modal layer, if any. */
function usePortalContainer() {
  return React.useContext(PortalContainerContext);
}

/**
 * Tracks the element a modal layer renders, to provide it through
 * `PortalContainerContext`. Returns the element and the ref to attach to it,
 * which also forwards the element to `forwardedRef`.
 */
function usePortalContainerRef<T extends HTMLElement>(
  forwardedRef: React.Ref<T> | undefined,
) {
  const [container, setContainer] = React.useState<T | null>(null);
  const ref = React.useCallback(
    (node: T | null) => {
      setContainer(node);
      if (typeof forwardedRef === "function") {
        const cleanup = forwardedRef(node);
        if (typeof cleanup === "function") {
          return () => {
            cleanup();
            setContainer(null);
          };
        }
      } else if (forwardedRef) {
        forwardedRef.current = node;
      }
    },
    [forwardedRef],
  );
  return [container, ref] as const;
}

/**
 * Wraps a modal layer's `onEscapeKeyDown` so Escape closes an open popup
 * inside the layer without also closing the layer. Radix hears Escape on the
 * document before the popup does, and the popup isn't a Radix layer, so Radix
 * would otherwise dismiss both.
 */
function keepLayerOpenForPopup(
  container: HTMLElement | null,
  onEscapeKeyDown: ((event: KeyboardEvent) => void) | undefined,
) {
  return (event: KeyboardEvent) => {
    if (container?.querySelector("[data-open]")) {
      event.preventDefault();
      return;
    }
    onEscapeKeyDown?.(event);
  };
}

export {
  keepLayerOpenForPopup,
  PortalContainerContext,
  usePortalContainer,
  usePortalContainerRef,
};
